import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import Toast from '../components/Toast';
import ConfirmModal from '../components/ConfirmModal';
import ManageUserAccessModal from '../components/ManageUserAccessModal';
import { KpiTile, MiniPill } from '../components/ui';

const PAY_DAY_LABELS = {
  last_working_day: 'Last working day',
  last_day_of_month: 'Last day of month',
  specific_date: 'Specific date',
  last_friday: 'Last Friday',
};

function InviteClientModal({ onClose, onSuccess }) {
  const [form, setForm] = useState({
    company_name: '', contact_first_name: '', contact_last_name: '', email: '',
    pay_day_type: 'last_working_day', pay_day_specific_date: '', pay_frequency: 'monthly',
    pay_day_adjust: true,
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e, sendInvite = true) => {
    e.preventDefault();
    if (!form.company_name) {
      setError('Company name is required');
      return;
    }
    if (form.email && !form.contact_first_name) {
      setError('First name is required when providing an email');
      return;
    }
    if (form.pay_day_type === 'specific_date' && (!form.pay_day_specific_date || form.pay_day_specific_date < 1 || form.pay_day_specific_date > 28)) {
      setError('Specific pay date must be between 1 and 28');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      await api.post('/admin/invite-client', { ...form, send_invite: sendInvite });
      onSuccess(sendInvite ? 'Invitation sent successfully!' : 'Client created as draft — invite not sent yet.');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create client');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6 max-h-[90vh] overflow-y-auto">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">New Client</h3>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Company Name *</label>
            <input value={form.company_name}
              onChange={(e) => setForm(prev => ({ ...prev, company_name: e.target.value }))}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-brand-500 focus:border-brand-500" />
          </div>
          <div className="border-t pt-4 mt-4">
            <p className="text-xs text-gray-400 mb-3">Optional — add a primary contact now, or assign users later via "Invite User"</p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700">First Name {form.email && '*'}</label>
                <input value={form.contact_first_name}
                  onChange={(e) => setForm(prev => ({ ...prev, contact_first_name: e.target.value }))}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-brand-500 focus:border-brand-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Surname</label>
                <input value={form.contact_last_name}
                  onChange={(e) => setForm(prev => ({ ...prev, contact_last_name: e.target.value }))}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-brand-500 focus:border-brand-500" />
              </div>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Email</label>
            <input type="email" value={form.email}
              onChange={(e) => setForm(prev => ({ ...prev, email: e.target.value }))}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-brand-500 focus:border-brand-500" />
          </div>
          {/* Pay Day Schedule */}
          <div className="border-t pt-4 mt-4">
            <h4 className="text-sm font-semibold text-gray-700 mb-3">Pay Day Schedule</h4>
            <div>
              <label className="block text-sm font-medium text-gray-700">Pay Day</label>
              <select value={form.pay_day_type}
                onChange={(e) => setForm(prev => ({ ...prev, pay_day_type: e.target.value, pay_day_specific_date: '' }))}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-brand-500 focus:border-brand-500">
                <option value="last_working_day">Last working day of the month</option>
                <option value="last_day_of_month">Last day of the month</option>
                <option value="specific_date">Specific date each month</option>
                <option value="last_friday">Last Friday of the month</option>
              </select>
            </div>
            {form.pay_day_type === 'specific_date' && (
              <div className="mt-3">
                <label className="block text-sm font-medium text-gray-700">Day of Month (1-28)</label>
                <input type="number" min="1" max="28" value={form.pay_day_specific_date}
                  onChange={(e) => setForm(prev => ({ ...prev, pay_day_specific_date: parseInt(e.target.value) || '' }))}
                  placeholder="e.g. 25"
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-brand-500 focus:border-brand-500" />
              </div>
            )}
            {form.pay_day_type !== 'last_working_day' && (
              <label className="mt-3 flex items-start gap-2 cursor-pointer">
                <input type="checkbox" checked={form.pay_day_adjust}
                  onChange={(e) => setForm(prev => ({ ...prev, pay_day_adjust: e.target.checked }))}
                  className="mt-0.5 rounded border-gray-300 text-brand-600 focus:ring-brand-500" />
                <span className="text-sm text-gray-700">
                  Move to previous working day
                  <span className="block text-xs text-gray-400">If pay day falls on a weekend or bank holiday, bring it forward to the previous working day.</span>
                </span>
              </label>
            )}
            <div className="mt-3">
              <label className="block text-sm font-medium text-gray-700">Pay Frequency</label>
              <select value={form.pay_frequency}
                onChange={(e) => setForm(prev => ({ ...prev, pay_frequency: e.target.value }))}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-brand-500 focus:border-brand-500">
                <option value="monthly">Monthly</option>
                <option value="weekly">Weekly</option>
                <option value="fortnightly">Fortnightly</option>
                <option value="4_weekly">4-Weekly</option>
              </select>
            </div>
          </div>

          {error && <p className="text-red-500 text-sm">{error}</p>}
          <div className="flex justify-end space-x-3 pt-2">
            <button type="button" onClick={onClose}
              className="px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200">
              Cancel
            </button>
            <button type="button" onClick={(e) => handleSubmit(e, false)} disabled={submitting}
              className="px-4 py-2 text-sm text-brand-700 bg-brand-50 rounded-md hover:bg-brand-100 disabled:opacity-50">
              {submitting ? 'Creating...' : form.email ? 'Create as Draft' : 'Create Client'}
            </button>
            {form.email && (
              <button type="submit" disabled={submitting}
                className="px-4 py-2 text-sm text-white bg-brand-600 rounded-md hover:bg-brand-700 disabled:opacity-50">
                {submitting ? 'Sending...' : 'Create & Send Invite'}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}

function InviteUserModal({ companies, onClose, onSuccess }) {
  const [form, setForm] = useState({ first_name: '', last_name: '', email: '', role: 'user' });
  const [selectedCompanyIds, setSelectedCompanyIds] = useState(new Set());
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [companySearch, setCompanySearch] = useState('');

  // Existing user detection
  const [existingUser, setExistingUser] = useState(null);
  const [searchingUser, setSearchingUser] = useState(false);
  const searchTimeout = React.useRef(null);

  const handleEmailChange = (email) => {
    setForm(prev => ({ ...prev, email }));
    setExistingUser(null);
    setError('');

    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    if (email.length >= 3 && email.includes('@')) {
      searchTimeout.current = setTimeout(async () => {
        setSearchingUser(true);
        try {
          const res = await api.get('/admin/users/search', { params: { email } });
          const exact = res.data.find(u => u.email.toLowerCase() === email.toLowerCase());
          if (exact) {
            setExistingUser(exact);
            setForm(prev => ({
              ...prev,
              first_name: exact.first_name || exact.name?.split(' ')[0] || '',
              last_name: exact.last_name || exact.name?.split(' ').slice(1).join(' ') || '',
            }));
          }
        } catch (err) { /* ignore search errors */ }
        finally { setSearchingUser(false); }
      }, 400);
    }
  };

  const toggleCompany = (companyId) => {
    setSelectedCompanyIds(prev => {
      const next = new Set(prev);
      if (next.has(companyId)) next.delete(companyId);
      else next.add(companyId);
      return next;
    });
  };

  const selectedCompanies = companies.filter(c => selectedCompanyIds.has(c.id));
  const filteredCompanies = companies.filter(c =>
    c.company_name.toLowerCase().includes(companySearch.toLowerCase())
  );

  const handleReviewClick = (e) => {
    e.preventDefault();
    if (selectedCompanyIds.size === 0) {
      setError('Please select at least one client');
      return;
    }
    if (!existingUser && (!form.first_name || !form.email)) {
      setError('First name and email are required');
      return;
    }
    setError('');
    setConfirming(true);
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setError('');
    try {
      if (existingUser) {
        await api.post('/admin/assign-user', {
          user_id: existingUser.id,
          company_ids: Array.from(selectedCompanyIds),
        });
      } else {
        await api.post('/admin/invite-user', {
          ...form,
          company_ids: Array.from(selectedCompanyIds),
        });
      }
      onSuccess();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to process request');
      setConfirming(false);
    } finally {
      setSubmitting(false);
    }
  };

  const displayName = existingUser
    ? (existingUser.first_name ? `${existingUser.first_name} ${existingUser.last_name || ''}`.trim() : existingUser.name)
    : `${form.first_name} ${form.last_name}`.trim();

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-lg w-full mx-4 p-6 max-h-[90vh] flex flex-col">
        <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4">
          {existingUser ? 'Assign User to Clients' : 'Invite User to Clients'}
        </h3>

        {confirming ? (
          <div className="flex-1 overflow-y-auto">
            <div className="p-4 bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-700 rounded-lg mb-4">
              <h4 className="text-sm font-semibold text-amber-800 dark:text-amber-300 mb-3">Please confirm:</h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500 dark:text-gray-400">User:</span>
                  <span className="font-medium text-gray-800 dark:text-gray-100">{displayName}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500 dark:text-gray-400">Email:</span>
                  <span className="text-gray-800 dark:text-gray-100">{form.email}</span>
                </div>
                {!existingUser && (
                  <div className="flex justify-between">
                    <span className="text-gray-500 dark:text-gray-400">Role:</span>
                    <span className="text-gray-800 dark:text-gray-100">{form.role === 'company_admin' ? 'Admin' : 'User'}</span>
                  </div>
                )}
                {existingUser && (
                  <div className="flex justify-between">
                    <span className="text-gray-500 dark:text-gray-400">Status:</span>
                    <span className="text-blue-600 dark:text-blue-400 font-medium">Existing user — no invite email will be sent</span>
                  </div>
                )}
              </div>

              <div className="mt-3 pt-3 border-t border-amber-200 dark:border-amber-700">
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase mb-2">
                  {existingUser ? 'Assigning access to:' : 'Sending invite for:'}
                </p>
                {selectedCompanies.map(c => (
                  <div key={c.id} className="flex items-center space-x-2 py-1">
                    <span className="w-2 h-2 rounded-full bg-green-500 flex-shrink-0"></span>
                    <span className="text-sm text-gray-800 dark:text-gray-100">{c.company_name}</span>
                  </div>
                ))}
              </div>
            </div>

            {error && <p className="text-red-500 text-sm mb-3">{error}</p>}

            <div className="flex justify-end space-x-3">
              <button type="button" onClick={() => setConfirming(false)}
                className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600">
                Go Back
              </button>
              <button type="button" onClick={handleSubmit} disabled={submitting}
                className="px-4 py-2 text-sm text-white bg-brand-600 rounded-md hover:bg-brand-700 disabled:opacity-50">
                {submitting ? 'Processing...' : (existingUser ? 'Confirm Assignment' : 'Send Invitation')}
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleReviewClick} className="flex-1 flex flex-col overflow-hidden">
            {/* Email field */}
            <div className="mb-3">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Email *</label>
              <div className="relative">
                <input type="email" value={form.email}
                  onChange={(e) => handleEmailChange(e.target.value)}
                  placeholder="user@example.com"
                  className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 shadow-sm focus:ring-brand-500 focus:border-brand-500" />
                {searchingUser && (
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">Checking...</span>
                )}
              </div>
              {existingUser && (
                <div className="mt-1.5 p-2 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-700 rounded text-sm">
                  <span className="text-blue-700 dark:text-blue-300 font-medium">Existing user found:</span>{' '}
                  <span className="text-blue-600 dark:text-blue-400">
                    {existingUser.first_name || existingUser.name} — currently on {existingUser.company_name}
                  </span>
                  <p className="text-xs text-blue-500 dark:text-blue-400 mt-0.5">They will be assigned to the selected clients below (no invite email sent).</p>
                </div>
              )}
            </div>

            {/* Name fields and role — only for new users */}
            {!existingUser && (
              <div className="grid grid-cols-3 gap-3 mb-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">First Name *</label>
                  <input value={form.first_name}
                    onChange={(e) => setForm(prev => ({ ...prev, first_name: e.target.value }))}
                    className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 shadow-sm focus:ring-brand-500 focus:border-brand-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Surname</label>
                  <input value={form.last_name}
                    onChange={(e) => setForm(prev => ({ ...prev, last_name: e.target.value }))}
                    className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 shadow-sm focus:ring-brand-500 focus:border-brand-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Role</label>
                  <select value={form.role}
                    onChange={(e) => setForm(prev => ({ ...prev, role: e.target.value }))}
                    className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 shadow-sm focus:ring-brand-500 focus:border-brand-500">
                    <option value="user">User</option>
                    <option value="company_admin">Admin</option>
                  </select>
                </div>
              </div>
            )}

            {/* Multi-select companies */}
            <div className="mb-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Select Clients *</label>
              <input
                type="text"
                value={companySearch}
                onChange={(e) => setCompanySearch(e.target.value)}
                placeholder="Search clients..."
                className="block w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 shadow-sm text-sm px-3 py-1.5 focus:ring-brand-500 focus:border-brand-500"
              />
            </div>

            <div className="flex-1 overflow-y-auto border border-gray-200 dark:border-gray-600 rounded-lg divide-y divide-gray-100 dark:divide-gray-700 mb-3 max-h-48">
              {filteredCompanies.length === 0 ? (
                <p className="text-sm text-gray-400 dark:text-gray-500 p-3">No clients match your search.</p>
              ) : (
                filteredCompanies.map(c => (
                  <label key={c.id}
                    className="flex items-center px-3 py-2 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700">
                    <input
                      type="checkbox"
                      checked={selectedCompanyIds.has(c.id)}
                      onChange={() => toggleCompany(c.id)}
                      className="rounded border-gray-300 dark:border-gray-600 text-brand-600 focus:ring-brand-500 mr-3"
                    />
                    <span className="text-sm text-gray-800 dark:text-gray-100">{c.company_name}</span>
                  </label>
                ))
              )}
            </div>

            <div className="text-xs text-gray-400 dark:text-gray-500 mb-3">
              {selectedCompanyIds.size} client{selectedCompanyIds.size !== 1 ? 's' : ''} selected
            </div>

            {error && <p className="text-red-500 text-sm mb-3">{error}</p>}

            <div className="flex justify-end space-x-3 pt-2">
              <button type="button" onClick={onClose}
                className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600">
                Cancel
              </button>
              <button type="submit" disabled={submitting}
                className="px-4 py-2 text-sm text-white bg-brand-600 rounded-md hover:bg-brand-700 disabled:opacity-50">
                Review &amp; Confirm
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

const PAY_DAY_TYPE_LABELS = {
  last_working_day: 'Last working day of the month',
  last_day_of_month: 'Last day of the month',
  specific_date: 'Specific date each month',
  last_friday: 'Last Friday of the month',
};

const PAY_FREQ_LABELS = {
  monthly: 'Monthly', weekly: 'Weekly', fortnightly: 'Fortnightly', '4_weekly': '4-Weekly',
};

// The client's house policy for auto-enrolment postponement. Recording it here is
// what lets the starter form warn their submitter when a starter contradicts it —
// with no policy set, nothing changes and nothing is flagged.
const POSTPONEMENT_OPTIONS = [
  { value: '', label: 'No policy recorded', desc: 'Nothing is flagged. The starter form behaves as it always has.' },
  { value: '0', label: 'Enrol from day one', desc: 'No postponement. Contributions start on the first pay run.' },
  { value: '1', label: 'Postpone 1 month', desc: 'Assessment deferred for 1 month from the start date.' },
  { value: '2', label: 'Postpone 2 months', desc: 'Assessment deferred for 2 months from the start date.' },
  { value: '3', label: 'Postpone 3 months', desc: 'Assessment deferred for 3 months — the legal maximum.' },
];

function PensionPolicyModal({ company, onClose, onSuccess }) {
  const initial = company.pension_postponement_months === null || company.pension_postponement_months === undefined
    ? ''
    : String(company.pension_postponement_months);
  const [value, setValue] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      await api.patch(`/admin/companies/${company.id}/pension-policy`, {
        pension_postponement_months: value === '' ? null : parseInt(value, 10),
      });
      onSuccess();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update pension policy');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
        <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-1">Pension Policy</h3>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">{company.company_name}</p>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            {POSTPONEMENT_OPTIONS.map((opt) => {
              const selected = value === opt.value;
              return (
                <button key={opt.value} type="button" onClick={() => setValue(opt.value)}
                  className={`w-full text-left rounded-lg ring-1 p-3 transition ${
                    selected
                      ? 'bg-brand-50 ring-brand-300 dark:bg-brand-900/30 dark:ring-brand-700'
                      : 'bg-white dark:bg-gray-700 ring-gray-200 dark:ring-gray-600 hover:ring-brand-200'
                  }`}>
                  <div className="flex items-center gap-2">
                    <span className={`grid place-items-center w-4 h-4 rounded-full ring-1 shrink-0 ${
                      selected ? 'bg-brand-600 ring-brand-600' : 'bg-white dark:bg-gray-600 ring-gray-300'
                    }`}>
                      {selected && <span className="w-1.5 h-1.5 rounded-full bg-white" />}
                    </span>
                    <span className={`text-[14px] font-semibold ${selected ? 'text-brand-800 dark:text-brand-200' : 'text-ink dark:text-gray-100'}`}>
                      {opt.label}
                    </span>
                  </div>
                  <p className="text-[12.5px] text-gray-500 dark:text-gray-400 mt-1 ml-6">{opt.desc}</p>
                </button>
              );
            })}
          </div>
          <p className="text-[12.5px] text-gray-500 dark:text-gray-400">
            When a new starter is submitted against this policy, the client is warned and has to give
            a reason. It never blocks them — the reason is flagged to you on the submission instead.
          </p>
          {error && <p className="text-red-500 text-sm">{error}</p>}
          <div className="flex justify-end space-x-3">
            <button type="button" onClick={onClose}
              className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600">
              Cancel
            </button>
            <button type="submit" disabled={submitting}
              className="px-4 py-2 text-sm text-white bg-brand-600 rounded-md hover:bg-brand-700 disabled:opacity-50">
              {submitting ? 'Saving...' : 'Save Policy'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function PayDayModal({ company, onClose, onSuccess }) {
  const [form, setForm] = useState({
    pay_day_type: company.pay_day_type || 'last_working_day',
    pay_day_specific_date: company.pay_day_specific_date || '',
    pay_frequency: company.pay_frequency || 'monthly',
    deadline_days_before: company.deadline_days_before || '',
    pay_day_adjust: company.pay_day_adjust !== false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState('');

  const formatPayDayDesc = (type, specificDate) => {
    if (type === 'specific_date' && specificDate) {
      const d = parseInt(specificDate);
      const suffix = d === 1 || d === 21 ? 'st' : d === 2 || d === 22 ? 'nd' : d === 3 || d === 23 ? 'rd' : 'th';
      return `${d}${suffix} of each month`;
    }
    return PAY_DAY_TYPE_LABELS[type] || type;
  };

  const handleRequestConfirm = (e) => {
    e.preventDefault();
    if (form.pay_day_type === 'specific_date' && (!form.pay_day_specific_date || form.pay_day_specific_date < 1 || form.pay_day_specific_date > 28)) {
      setError('Specific date must be between 1 and 28');
      return;
    }
    setError('');
    setConfirming(true);
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setError('');
    try {
      await api.patch(`/admin/companies/${company.id}/pay-day`, form);
      onSuccess();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update pay day');
      setConfirming(false);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
        <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-1">Pay Day Schedule</h3>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">{company.company_name}</p>
        {confirming ? (
          <div className="space-y-4">
            <div className="p-4 bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-700 rounded-lg">
              <h4 className="text-sm font-semibold text-amber-800 dark:text-amber-300 mb-3">Please confirm this change:</h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500 dark:text-gray-400">Pay Day:</span>
                  <div className="text-right">
                    {form.pay_day_type !== company.pay_day_type || form.pay_day_specific_date !== company.pay_day_specific_date ? (
                      <><span className="text-gray-400 dark:text-gray-500 line-through mr-2">{formatPayDayDesc(company.pay_day_type, company.pay_day_specific_date)}</span>
                      <span className="font-medium text-gray-900 dark:text-gray-100">{formatPayDayDesc(form.pay_day_type, form.pay_day_specific_date)}</span></>
                    ) : (
                      <span className="text-gray-700 dark:text-gray-300">{formatPayDayDesc(form.pay_day_type, form.pay_day_specific_date)}</span>
                    )}
                  </div>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500 dark:text-gray-400">Frequency:</span>
                  <div className="text-right">
                    {form.pay_frequency !== (company.pay_frequency || 'monthly') ? (
                      <><span className="text-gray-400 dark:text-gray-500 line-through mr-2">{PAY_FREQ_LABELS[company.pay_frequency] || 'Monthly'}</span>
                      <span className="font-medium text-gray-900 dark:text-gray-100">{PAY_FREQ_LABELS[form.pay_frequency]}</span></>
                    ) : (
                      <span className="text-gray-700 dark:text-gray-300">{PAY_FREQ_LABELS[form.pay_frequency]}</span>
                    )}
                  </div>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500 dark:text-gray-400">Deadline:</span>
                  <span className="text-gray-700 dark:text-gray-300">{form.deadline_days_before || 5} working days before pay day</span>
                </div>
                {form.pay_day_type !== 'last_working_day' && (
                  <div className="flex justify-between">
                    <span className="text-gray-500 dark:text-gray-400">Weekend / bank holiday:</span>
                    <span className="text-gray-700 dark:text-gray-300">{form.pay_day_adjust ? 'Move to previous working day' : 'Keep on the date'}</span>
                  </div>
                )}
              </div>
            </div>
            {error && <p className="text-red-500 text-sm">{error}</p>}
            <div className="flex justify-end space-x-3">
              <button type="button" onClick={() => setConfirming(false)}
                className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600">
                Go Back
              </button>
              <button type="button" onClick={handleSubmit} disabled={submitting}
                className="px-4 py-2 text-sm text-white bg-brand-600 rounded-md hover:bg-brand-700 disabled:opacity-50">
                {submitting ? 'Saving...' : 'Confirm Change'}
              </button>
            </div>
          </div>
        ) : (
        <form onSubmit={handleRequestConfirm} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Pay Day</label>
            <select value={form.pay_day_type}
              onChange={(e) => setForm(prev => ({ ...prev, pay_day_type: e.target.value, pay_day_specific_date: '' }))}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-brand-500 focus:border-brand-500">
              <option value="last_working_day">Last working day of the month</option>
              <option value="last_day_of_month">Last day of the month</option>
              <option value="specific_date">Specific date each month</option>
              <option value="last_friday">Last Friday of the month</option>
            </select>
          </div>
          {form.pay_day_type === 'specific_date' && (
            <div>
              <label className="block text-sm font-medium text-gray-700">Day of Month (1-28)</label>
              <input type="number" min="1" max="28" value={form.pay_day_specific_date}
                onChange={(e) => setForm(prev => ({ ...prev, pay_day_specific_date: parseInt(e.target.value) || '' }))}
                placeholder="e.g. 25"
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-brand-500 focus:border-brand-500" />
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700">Pay Frequency</label>
            <select value={form.pay_frequency}
              onChange={(e) => setForm(prev => ({ ...prev, pay_frequency: e.target.value }))}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-brand-500 focus:border-brand-500">
              <option value="monthly">Monthly</option>
              <option value="weekly">Weekly</option>
              <option value="fortnightly">Fortnightly</option>
              <option value="4_weekly">4-Weekly</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Submission Deadline (working days before pay day)</label>
            <input type="number" min="1" max="20" value={form.deadline_days_before}
              onChange={(e) => setForm(prev => ({ ...prev, deadline_days_before: parseInt(e.target.value) || '' }))}
              placeholder="Default: 5"
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-brand-500 focus:border-brand-500" />
            <p className="text-xs text-gray-400 mt-1">Leave blank to use the global default (5 working days).</p>
          </div>
          {form.pay_day_type !== 'last_working_day' && (
            <label className="flex items-start gap-2 cursor-pointer">
              <input type="checkbox" checked={form.pay_day_adjust}
                onChange={(e) => setForm(prev => ({ ...prev, pay_day_adjust: e.target.checked }))}
                className="mt-0.5 rounded border-gray-300 text-brand-600 focus:ring-brand-500" />
              <span className="text-sm text-gray-700">
                Move to previous working day
                <span className="block text-xs text-gray-400">If pay day falls on a weekend or bank holiday, bring it forward to the previous working day (and schedule reminders accordingly).</span>
              </span>
            </label>
          )}
          {error && <p className="text-red-500 text-sm">{error}</p>}
          <div className="flex justify-end space-x-3 pt-2">
            <button type="button" onClick={onClose}
              className="px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200">
              Cancel
            </button>
            <button type="submit" disabled={submitting}
              className="px-4 py-2 text-sm text-white bg-brand-600 rounded-md hover:bg-brand-700 disabled:opacity-50">
              {submitting ? 'Saving...' : 'Save Pay Day'}
            </button>
          </div>
        </form>
        )}
      </div>
    </div>
  );
}

// All employee fields for CSV import
const ALL_EMPLOYEE_FIELDS = [
  { key: 'first_name', label: 'First Name', required: true, group: 'personal' },
  { key: 'last_name', label: 'Last Name', required: true, group: 'personal' },
  { key: 'email', label: 'Email', group: 'personal' },
  { key: 'gender', label: 'Gender', group: 'personal' },
  { key: 'date_of_birth', label: 'Date of Birth', group: 'personal' },
  { key: 'ni_number', label: 'NI Number', group: 'personal' },
  { key: 'address', label: 'Address Line 1', group: 'address' },
  { key: 'address_line_2', label: 'Address Line 2', group: 'address' },
  { key: 'city', label: 'City', group: 'address' },
  { key: 'postcode', label: 'Postcode', group: 'address' },
  { key: 'start_date', label: 'Start Date', group: 'employment' },
  { key: 'job_title', label: 'Job Title', group: 'employment' },
  { key: 'annual_salary', label: 'Annual Salary', group: 'employment' },
  { key: 'prorata_salary', label: 'Pro-Rata Salary', group: 'employment' },
  { key: 'pay_frequency', label: 'Pay Frequency', group: 'employment' },
  { key: 'contract_type', label: 'Contract Type', group: 'employment' },
  { key: 'contract_end_date', label: 'Contract End Date', group: 'employment' },
  { key: 'part_time_basis', label: 'Part-Time Basis', group: 'employment' },
  { key: 'is_director', label: 'Director', group: 'employment' },
  { key: 'student_loan', label: 'Student Loan Plan', group: 'employment' },
  { key: 'commission', label: 'Commission', group: 'employment' },
  { key: 'pay_type', label: 'Pay Type', group: 'employment' },
  { key: 'hourly_rate', label: 'Hourly Rate', group: 'employment' },
  { key: 'contracted_hours_per_week', label: 'Contracted Hours/Week', group: 'employment' },
  { key: 'bank_account_name', label: 'Bank Account Name', group: 'bank' },
  { key: 'sort_code', label: 'Sort Code', group: 'bank' },
  { key: 'account_number', label: 'Account Number', group: 'bank' },
  { key: 'roll_number', label: 'Roll Number', group: 'bank' },
  { key: 'pension_status', label: 'Pension Status', group: 'pension' },
  { key: 'pension_employer_value', label: 'Employer Pension %', group: 'pension' },
  { key: 'pension_employee_value', label: 'Employee Pension %', group: 'pension' },
  { key: 'pension_earnings_basis', label: 'Pension Earnings Basis', group: 'pension' },
];

const EMPTY_ROW = Object.fromEntries(ALL_EMPLOYEE_FIELDS.map(f => [f.key, '']));

// Header aliases for auto-matching CSV columns to fields
const HEADER_ALIASES = {
  first_name: ['first_name', 'firstname', 'first name', 'forename'],
  last_name: ['last_name', 'lastname', 'last name', 'surname', 'family name'],
  email: ['email', 'email address', 'e-mail'],
  gender: ['gender', 'sex'],
  date_of_birth: ['date_of_birth', 'dob', 'date of birth', 'birth date', 'birthdate'],
  ni_number: ['ni_number', 'ni number', 'national insurance', 'national insurance number', 'nino'],
  address: ['address', 'address_line_1', 'address line 1', 'address1', 'street'],
  address_line_2: ['address_line_2', 'address line 2', 'address2'],
  city: ['city', 'town', 'town/city'],
  postcode: ['postcode', 'post code', 'postal code', 'zip', 'zip code'],
  start_date: ['start_date', 'start date', 'employment start date', 'joining date', 'date started'],
  job_title: ['job_title', 'job title', 'position', 'role title'],
  annual_salary: ['annual_salary', 'annual salary', 'salary', 'gross salary', 'yearly salary'],
  prorata_salary: ['prorata_salary', 'pro rata salary', 'pro-rata salary', 'prorata'],

  pay_frequency: ['pay_frequency', 'pay frequency', 'payment frequency'],
  contract_type: ['contract_type', 'contract type', 'contract'],
  contract_end_date: ['contract_end_date', 'contract end date', 'end date'],
  part_time_basis: ['part_time_basis', 'part time basis', 'pt basis', 'working pattern'],
  is_director: ['is_director', 'director', 'is director'],
  student_loan: ['student_loan', 'student loan', 'student loan plan', 'slc'],
  commission: ['commission', 'commission amount'],
  pay_type: ['pay_type', 'pay type', 'payment type', 'salaried or hourly', 'hourly/salaried'],
  hourly_rate: ['hourly_rate', 'hourly rate', 'rate per hour', 'hour rate', 'rate/hr'],
  contracted_hours_per_week: ['contracted_hours_per_week', 'contracted hours', 'hours per week', 'weekly hours', 'contracted hours per week'],
  bank_account_name: ['bank_account_name', 'bank account name', 'account name', 'account holder'],
  sort_code: ['sort_code', 'sort code', 'bank sort code'],
  account_number: ['account_number', 'account number', 'bank account number', 'bank account'],
  roll_number: ['roll_number', 'roll number', 'building society roll number', 'building society reference'],
  pension_status: ['pension_status', 'pension status', 'pension', 'pension enrolment', 'pension enrollment', 'pension enrolment status'],
  pension_employer_value: ['pension_employer_value', 'employer pension', 'employer pension %', 'employer contribution', 'er pension', 'er %', 'er contribution'],
  pension_employee_value: ['pension_employee_value', 'employee pension', 'employee pension %', 'employee contribution', 'ee pension', 'ee %', 'ee contribution'],
  pension_earnings_basis: ['pension_earnings_basis', 'pension earnings basis', 'earnings basis', 'pension basis'],
};

// RFC 4180-compliant CSV parser — handles quoted fields, commas within quotes, escaped quotes
function parseCsvRfc(text) {
  const rows = [];
  let current = '';
  let inQuotes = false;
  let row = [];

  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    const next = text[i + 1];

    if (inQuotes) {
      if (ch === '"' && next === '"') {
        current += '"';
        i++; // skip escaped quote
      } else if (ch === '"') {
        inQuotes = false;
      } else {
        current += ch;
      }
    } else {
      if (ch === '"') {
        inQuotes = true;
      } else if (ch === ',') {
        row.push(current.trim());
        current = '';
      } else if (ch === '\n' || (ch === '\r' && next === '\n')) {
        row.push(current.trim());
        if (row.some(c => c !== '')) rows.push(row);
        row = [];
        current = '';
        if (ch === '\r') i++; // skip \n after \r
      } else {
        current += ch;
      }
    }
  }
  // Last row
  if (current || row.length > 0) {
    row.push(current.trim());
    if (row.some(c => c !== '')) rows.push(row);
  }
  return rows;
}

function autoMatchHeader(csvHeader) {
  const h = csvHeader.toLowerCase().trim().replace(/['"]/g, '');
  for (const [field, aliases] of Object.entries(HEADER_ALIASES)) {
    if (aliases.includes(h)) return field;
  }
  return '';
}

function generateCsvTemplate() {
  const headers = ALL_EMPLOYEE_FIELDS.map(f => f.label).join(',');
  const example = [
    'John', 'Smith', 'john@example.com', 'Male', '1990-01-15', 'AB123456C',
    '123 High Street', 'Flat 2', 'London', 'SW1A 1AA',
    '2024-04-01', 'Senior Developer', '45000', '', '1257L', 'monthly',
    'permanent', '', '', 'no', 'none', '',
    'John Smith', '12-34-56', '12345678', '',
    'enrolled', '3', '5', 'qualifying',
  ].join(',');
  return `${headers}\n${example}`;
}

function ImportEmployeeCard({ row, index, onUpdate, onRemove, canRemove }) {
  const [showMore, setShowMore] = useState(false);
  const u = (field, value) => onUpdate(index, field, value);
  const inp = "block w-full rounded border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 shadow-sm text-xs py-1.5";
  const isPartTime = row.contract_type === 'part-time';
  const isFixedTerm = row.contract_type === 'fixed-term';

  const [ptDays, setPtDays] = useState('');
  const handlePtDaysChange = (val) => {
    setPtDays(val);
    const days = parseFloat(val);
    const annual = parseFloat(row.annual_salary);
    if (days > 0 && days <= 5 && annual > 0) {
      const prorata = (annual * (days / 5)).toFixed(2);
      u('prorata_salary', prorata);
      if (!row.part_time_basis) u('part_time_basis', `${val} days/week`);
    }
  };

  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-3 bg-white dark:bg-gray-800">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-semibold text-gray-500 dark:text-gray-400">Employee {index + 1}</span>
        <div className="flex items-center space-x-2">
          <button type="button" onClick={() => setShowMore(!showMore)} className="text-xs text-brand-600 hover:text-brand-800">
            {showMore ? 'Show less' : 'Show all fields'}
          </button>
          {canRemove && <button onClick={() => onRemove(index)} className="text-red-400 hover:text-red-600 text-xs">&times; Remove</button>}
        </div>
      </div>
      <div className="grid grid-cols-4 gap-2 mb-2">
        <div><label className="block text-xs text-gray-400 mb-0.5">First Name *</label><input value={row.first_name} onChange={(e) => u('first_name', e.target.value)} placeholder="John" className={inp} /></div>
        <div><label className="block text-xs text-gray-400 mb-0.5">Last Name *</label><input value={row.last_name} onChange={(e) => u('last_name', e.target.value)} placeholder="Smith" className={inp} /></div>
        <div><label className="block text-xs text-gray-400 mb-0.5">Email</label><input value={row.email} onChange={(e) => u('email', e.target.value)} placeholder="email@example.com" className={inp} /></div>
        <div><label className="block text-xs text-gray-400 mb-0.5">NI Number</label><input value={row.ni_number} onChange={(e) => u('ni_number', e.target.value)} placeholder="AB123456C" className={inp} /></div>
      </div>
      <div className="grid grid-cols-4 gap-2">
        <div><label className="block text-xs text-gray-400 mb-0.5">Start Date</label><input type="date" value={row.start_date} onChange={(e) => u('start_date', e.target.value)} className={inp} /></div>
        <div><label className="block text-xs text-gray-400 mb-0.5">{isPartTime ? 'Annual Salary (FTE)' : 'Annual Salary'}</label><input type="number" value={row.annual_salary} onChange={(e) => u('annual_salary', e.target.value)} placeholder="30000" className={inp} /></div>
        <div><label className="block text-xs text-gray-400 mb-0.5">Job Title</label><input value={row.job_title} onChange={(e) => u('job_title', e.target.value)} placeholder="Title" className={inp} /></div>
        <div><label className="block text-xs text-gray-400 mb-0.5">Contract Type</label>
          <select value={row.contract_type} onChange={(e) => {
            u('contract_type', e.target.value);
            if (e.target.value !== 'fixed-term' && row.contract_end_date) u('contract_end_date', '');
          }} className={inp}>
            <option value="">Permanent</option>
            <option value="permanent">Permanent</option>
            <option value="fixed-term">Fixed Term</option>
            <option value="part-time">Part-Time</option>
          </select>
        </div>
        {isFixedTerm && (
          <div><label className="block text-xs text-gray-400 mb-0.5">Contract End Date *</label>
            <input type="date" value={row.contract_end_date} onChange={(e) => u('contract_end_date', e.target.value)}
              className={`${inp} ${!row.contract_end_date ? 'border-red-300 dark:border-red-700' : ''}`} />
          </div>
        )}
      </div>
      {isPartTime && (
        <div className="mt-3 p-3 bg-brand-50 dark:bg-brand-900/20 border border-brand-200 dark:border-brand-800 rounded">
          <p className="text-xs font-medium text-brand-700 dark:text-brand-300 mb-2">Part-Time Details</p>
          <div className="grid grid-cols-4 gap-2">
            <div>
              <label className="block text-xs text-gray-400 mb-0.5">Days per Week</label>
              <input type="number" step="0.5" min="0" max="5" value={ptDays} onChange={(e) => handlePtDaysChange(e.target.value)} placeholder="e.g. 4" className={inp} />
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-0.5">Part-Time Basis</label>
              <input value={row.part_time_basis} onChange={(e) => u('part_time_basis', e.target.value)} placeholder="e.g. 4 days/week" className={inp} />
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-0.5">Pro-Rata Salary</label>
              <input type="number" value={row.prorata_salary} onChange={(e) => u('prorata_salary', e.target.value)} placeholder="Auto-calc from days" className={inp} />
            </div>
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">Enter days per week to auto-calculate pro-rata from the FTE annual salary, or enter pro-rata directly.</p>
        </div>
      )}
      {showMore && (
        <>
          <div className="border-t border-gray-100 dark:border-gray-700 mt-3 pt-3">
            <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">Personal Details</p>
            <div className="grid grid-cols-4 gap-2">
              <div><label className="block text-xs text-gray-400 mb-0.5">Gender</label>
                <select value={row.gender} onChange={(e) => u('gender', e.target.value)} className={inp}>
                  <option value="">-</option><option value="male">Male</option><option value="female">Female</option>
                </select>
              </div>
              <div><label className="block text-xs text-gray-400 mb-0.5">Date of Birth</label><input type="date" value={row.date_of_birth} onChange={(e) => u('date_of_birth', e.target.value)} className={inp} /></div>
              <div><label className="block text-xs text-gray-400 mb-0.5">Student Loan</label>
                <select value={row.student_loan} onChange={(e) => u('student_loan', e.target.value)} className={inp}>
                  <option value="">None</option><option value="plan1">Plan 1</option><option value="plan2">Plan 2</option><option value="plan4">Plan 4</option><option value="postgrad">Postgrad</option>
                </select>
              </div>
              <div><label className="block text-xs text-gray-400 mb-0.5">Director</label>
                <select value={row.is_director} onChange={(e) => u('is_director', e.target.value)} className={inp}>
                  <option value="">No</option><option value="yes">Yes</option>
                </select>
              </div>
            </div>
          </div>
          <div className="border-t border-gray-100 dark:border-gray-700 mt-3 pt-3">
            <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">Employment</p>
            <div className="grid grid-cols-4 gap-2">
              {!isFixedTerm && <div><label className="block text-xs text-gray-400 mb-0.5">Contract End Date</label><input type="date" value={row.contract_end_date} onChange={(e) => u('contract_end_date', e.target.value)} className={inp} /></div>}
              <div><label className="block text-xs text-gray-400 mb-0.5">Pay Frequency</label>
                <select value={row.pay_frequency} onChange={(e) => u('pay_frequency', e.target.value)} className={inp}>
                  <option value="">Monthly</option><option value="monthly">Monthly</option><option value="weekly">Weekly</option><option value="4-weekly">4-Weekly</option>
                </select>
              </div>
              <div><label className="block text-xs text-gray-400 mb-0.5">Commission</label><input type="number" value={row.commission} onChange={(e) => u('commission', e.target.value)} placeholder="0" className={inp} /></div>
            </div>
          </div>
          <div className="border-t border-gray-100 dark:border-gray-700 mt-3 pt-3">
            <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">Address</p>
            <div className="grid grid-cols-4 gap-2">
              <div className="col-span-2"><label className="block text-xs text-gray-400 mb-0.5">Address Line 1</label><input value={row.address} onChange={(e) => u('address', e.target.value)} placeholder="123 High Street" className={inp} /></div>
              <div><label className="block text-xs text-gray-400 mb-0.5">Address Line 2</label><input value={row.address_line_2} onChange={(e) => u('address_line_2', e.target.value)} className={inp} /></div>
              <div className="grid grid-cols-2 gap-2">
                <div><label className="block text-xs text-gray-400 mb-0.5">City</label><input value={row.city} onChange={(e) => u('city', e.target.value)} className={inp} /></div>
                <div><label className="block text-xs text-gray-400 mb-0.5">Postcode</label><input value={row.postcode} onChange={(e) => u('postcode', e.target.value)} placeholder="SW1A 1AA" className={inp} /></div>
              </div>
            </div>
          </div>
          <div className="border-t border-gray-100 dark:border-gray-700 mt-3 pt-3">
            <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">Bank Details</p>
            <div className="grid grid-cols-4 gap-2">
              <div><label className="block text-xs text-gray-400 mb-0.5">Account Name</label><input value={row.bank_account_name} onChange={(e) => u('bank_account_name', e.target.value)} className={inp} /></div>
              <div><label className="block text-xs text-gray-400 mb-0.5">Sort Code</label><input value={row.sort_code} onChange={(e) => u('sort_code', e.target.value)} placeholder="12-34-56" className={inp} /></div>
              <div><label className="block text-xs text-gray-400 mb-0.5">Account Number</label><input value={row.account_number} onChange={(e) => u('account_number', e.target.value)} placeholder="12345678" className={inp} /></div>
              <div><label className="block text-xs text-gray-400 mb-0.5">Roll Number</label><input value={row.roll_number} onChange={(e) => u('roll_number', e.target.value)} className={inp} /></div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function ImportEmployeesModal({ companyId, companyName, onClose, onSuccess }) {
  const [mode, setMode] = useState('manual'); // manual, csv-upload, csv-map, csv-preview
  const [rows, setRows] = useState([{ ...EMPTY_ROW }]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);

  // CSV state
  const [csvHeaders, setCsvHeaders] = useState([]);
  const [csvData, setCsvData] = useState([]);
  const [headerMap, setHeaderMap] = useState({});

  const addRow = () => setRows([...rows, { ...EMPTY_ROW }]);
  const removeRow = (index) => { if (rows.length > 1) setRows(rows.filter((_, i) => i !== index)); };
  const updateRow = (index, field, value) => setRows(rows.map((r, i) => i === index ? { ...r, [field]: value } : r));

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const text = ev.target.result;
      const parsed = parseCsvRfc(text);
      if (parsed.length < 2) {
        setError('CSV must have a header row and at least one data row');
        return;
      }
      const headers = parsed[0];
      const data = parsed.slice(1);

      setCsvHeaders(headers);
      setCsvData(data);
      setError('');

      // Auto-match headers
      const autoMap = {};
      headers.forEach((h, i) => {
        const match = autoMatchHeader(h);
        if (match) autoMap[i] = match;
      });
      setHeaderMap(autoMap);
      setMode('csv-map');
    };
    reader.readAsText(file);
  };

  const handleMapChange = (colIndex, fieldKey) => {
    setHeaderMap(prev => {
      const next = { ...prev };
      // Remove any existing mapping to this field (prevent duplicates)
      if (fieldKey) {
        Object.keys(next).forEach(k => { if (next[k] === fieldKey) delete next[k]; });
      }
      if (fieldKey) next[colIndex] = fieldKey;
      else delete next[colIndex];
      return next;
    });
  };

  const mappedFields = new Set(Object.values(headerMap));
  const hasRequiredMapped = ALL_EMPLOYEE_FIELDS.filter(f => f.required).every(f => mappedFields.has(f.key));

  const applyMapping = () => {
    const mapped = csvData.map(dataRow => {
      const row = { ...EMPTY_ROW };
      Object.entries(headerMap).forEach(([colIdx, fieldKey]) => {
        const val = dataRow[parseInt(colIdx)];
        if (val !== undefined) {
          // Clean salary values - strip commas and currency symbols
          if (fieldKey === 'annual_salary' || fieldKey === 'prorata_salary' || fieldKey === 'commission') {
            row[fieldKey] = val.replace(/[£$,\s]/g, '');
          } else {
            row[fieldKey] = val;
          }
        }
      });
      return row;
    }).filter(r => r.first_name || r.last_name); // filter blank rows
    setRows(mapped);
    setMode('csv-preview');
  };

  const handleDownloadTemplate = () => {
    const csv = generateCsvTemplate();
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'employee_import_template.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const [validationErrors, setValidationErrors] = useState([]);

  const handleSubmit = async () => {
    const validRows = rows.filter(r => r.first_name && r.last_name);
    if (validRows.length === 0) { setError('At least one employee with first name and last name is required'); return; }

    // Catch fixed-term rows with no end date before the round-trip, using the same
    // per-row shape the server returns so the existing error screen renders it. Only
    // the presence rule is checked here — whether the date falls after the start date
    // is left to the server, which parses UK/ISO/US date formats properly.
    const localErrors = [];
    validRows.forEach((r, i) => {
      const contractType = String(r.contract_type || '').trim().toLowerCase().replace(/\s+/g, '-');
      if (contractType === 'fixed-term' && !String(r.contract_end_date || '').trim()) {
        localErrors.push({
          row: i + 1,
          name: `${r.first_name || ''} ${r.last_name || ''}`.trim() || `Row ${i + 1}`,
          errors: ['Contract end date is required for a fixed-term contract'],
        });
      }
    });
    if (localErrors.length > 0) {
      setValidationErrors(localErrors);
      setError(`${localErrors.length} of ${validRows.length} row(s) have errors. Fix the issues below and try again.`);
      return;
    }

    setSubmitting(true); setError(''); setValidationErrors([]);
    try {
      const res = await api.post(`/admin/companies/${companyId}/employees/import`, { employees: validRows });
      setResult(res.data);
    } catch (err) {
      const data = err.response?.data;
      if (data?.validation_errors) {
        setValidationErrors(data.validation_errors);
        setError(`${data.failed} of ${data.total} row(s) have errors. Fix the issues below and try again.`);
      } else {
        setError(data?.error || 'Failed to import employees');
      }
    }
    finally { setSubmitting(false); }
  };

  // VALIDATION ERRORS screen
  if (validationErrors.length > 0) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full mx-4 p-6 max-h-[90vh] overflow-y-auto">
          <h3 className="text-lg font-semibold text-red-600 dark:text-red-400 mb-2">Import Errors</h3>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">{error}</p>
          <div className="space-y-3 mb-4">
            {validationErrors.map((ve, i) => (
              <div key={i} className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                <p className="text-sm font-medium text-red-800 dark:text-red-300 mb-1">
                  Row {ve.row}: {ve.name}
                </p>
                <ul className="list-disc list-inside text-sm text-red-600 dark:text-red-400 space-y-0.5">
                  {ve.errors.map((e, j) => <li key={j}>{e}</li>)}
                </ul>
              </div>
            ))}
          </div>
          <div className="flex justify-end space-x-3">
            <button onClick={onClose}
              className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600">
              Cancel
            </button>
            <button onClick={() => { setValidationErrors([]); setError(''); }}
              className="px-4 py-2 text-sm text-white bg-brand-600 rounded-md hover:bg-brand-700">
              {mode === 'manual' ? 'Back to Employees' : 'Back to Preview'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // SUCCESS screen
  if (result) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4 p-6 text-center">
          <div className="text-green-600 text-4xl mb-3">&#10003;</div>
          <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-2">Import Complete</h3>
          <p className="text-gray-600 dark:text-gray-400 mb-4">{result.imported} employee(s) imported to {companyName}.</p>
          <button onClick={() => { onSuccess(); }} className="px-6 py-2 text-sm text-white bg-brand-600 rounded-md hover:bg-brand-700">Done</button>
        </div>
      </div>
    );
  }

  // HEADER MAPPING screen
  if (mode === 'csv-map') {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full mx-4 p-6 max-h-[90vh] overflow-y-auto">
          <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-1">Map CSV Columns</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
            Match each CSV column to the correct employee field. {csvData.length} row(s) detected.
          </p>

          <div className="overflow-x-auto border border-gray-200 dark:border-gray-600 rounded-lg mb-4">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">CSV Column</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Maps To</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Sample (Row 1)</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Sample (Row 2)</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {csvHeaders.map((header, colIdx) => (
                  <tr key={colIdx} className={headerMap[colIdx] ? 'bg-green-50 dark:bg-green-900/10' : ''}>
                    <td className="px-3 py-2 font-medium text-gray-800 dark:text-gray-200">{header}</td>
                    <td className="px-3 py-2">
                      <select
                        value={headerMap[colIdx] || ''}
                        onChange={(e) => handleMapChange(colIdx, e.target.value)}
                        className={`block w-full rounded border text-xs py-1.5 ${
                          headerMap[colIdx]
                            ? 'border-green-300 dark:border-green-600 bg-white dark:bg-gray-700 dark:text-gray-100'
                            : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 dark:text-gray-400'
                        }`}
                      >
                        <option value="">-- Skip this column --</option>
                        {ALL_EMPLOYEE_FIELDS.map(f => (
                          <option key={f.key} value={f.key} disabled={mappedFields.has(f.key) && headerMap[colIdx] !== f.key}>
                            {f.label}{f.required ? ' *' : ''}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="px-3 py-2 text-gray-600 dark:text-gray-400 text-xs truncate max-w-[150px]">{csvData[0]?.[colIdx] || '-'}</td>
                    <td className="px-3 py-2 text-gray-600 dark:text-gray-400 text-xs truncate max-w-[150px]">{csvData[1]?.[colIdx] || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {!hasRequiredMapped && (
            <p className="text-amber-600 dark:text-amber-400 text-sm mb-3">
              Required fields not yet mapped: {ALL_EMPLOYEE_FIELDS.filter(f => f.required && !mappedFields.has(f.key)).map(f => f.label).join(', ')}
            </p>
          )}

          <div className="text-xs text-gray-400 dark:text-gray-500 mb-3">
            {Object.keys(headerMap).length} of {csvHeaders.length} columns mapped
          </div>

          {error && <p className="text-red-500 text-sm mb-3">{error}</p>}

          <div className="flex justify-end space-x-3">
            <button onClick={() => { setMode('manual'); setCsvHeaders([]); setCsvData([]); }}
              className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600">Back</button>
            <button onClick={applyMapping} disabled={!hasRequiredMapped}
              className="px-4 py-2 text-sm text-white bg-brand-600 rounded-md hover:bg-brand-700 disabled:opacity-50 disabled:cursor-not-allowed">
              Preview Data
            </button>
          </div>
        </div>
      </div>
    );
  }

  // DATA PREVIEW screen
  if (mode === 'csv-preview') {
    const validRows = rows.filter(r => r.first_name && r.last_name);
    const skippedCount = rows.length - validRows.length;
    const mappedFieldKeys = Object.values(headerMap);
    const previewFields = ALL_EMPLOYEE_FIELDS.filter(f => mappedFieldKeys.includes(f.key));

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-5xl w-full mx-4 p-6 max-h-[90vh] overflow-y-auto">
          <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-1">Review Import Data</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
            {validRows.length} employee(s) ready to import to {companyName}.
            {skippedCount > 0 && <span className="text-amber-500"> ({skippedCount} rows skipped — missing first or last name)</span>}
          </p>

          <div className="overflow-x-auto border border-gray-200 dark:border-gray-600 rounded-lg mb-4">
            <table className="min-w-full text-xs">
              <thead className="bg-gray-50 dark:bg-gray-700 sticky top-0">
                <tr>
                  <th className="px-2 py-2 text-left font-medium text-gray-500 dark:text-gray-400">#</th>
                  {previewFields.map(f => (
                    <th key={f.key} className="px-2 py-2 text-left font-medium text-gray-500 dark:text-gray-400 whitespace-nowrap">
                      {f.label}{f.required ? ' *' : ''}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {validRows.map((row, i) => (
                  <tr key={i} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                    <td className="px-2 py-1.5 text-gray-400">{i + 1}</td>
                    {previewFields.map(f => (
                      <td key={f.key} className={`px-2 py-1.5 whitespace-nowrap ${
                        f.required && !row[f.key] ? 'text-red-500' : 'text-gray-800 dark:text-gray-200'
                      }`}>
                        {row[f.key] || (f.required ? 'MISSING' : '-')}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {error && <p className="text-red-500 text-sm mb-3">{error}</p>}

          <div className="flex justify-end space-x-3">
            <button onClick={() => setMode('csv-map')}
              className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600">Back to Mapping</button>
            <button onClick={handleSubmit} disabled={submitting || validRows.length === 0}
              className="px-4 py-2 text-sm text-white bg-brand-600 rounded-md hover:bg-brand-700 disabled:opacity-50">
              {submitting ? 'Importing...' : `Import ${validRows.length} Employee(s)`}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // MAIN screen — manual entry + CSV upload
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full mx-4 p-6 max-h-[90vh] overflow-y-auto">
        <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-1">Import Employees - {companyName}</h3>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">Add existing employees before the client accepts their invite. Include address and bank details so the client can verify them.</p>
        <div className="flex items-center space-x-2 mb-4">
          <button onClick={() => setMode('manual')} className={`px-3 py-1.5 text-sm rounded ${mode === 'manual' ? 'bg-brand-600 text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'}`}>Manual Entry</button>
          <button onClick={() => setMode('csv-upload')} className={`px-3 py-1.5 text-sm rounded ${mode === 'csv-upload' ? 'bg-brand-600 text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'}`}>CSV Upload</button>
          <button onClick={handleDownloadTemplate} className="ml-auto px-3 py-1.5 text-xs text-brand-600 dark:text-brand-400 hover:text-brand-800 dark:hover:text-brand-300 font-medium">
            Download CSV Template
          </button>
        </div>
        {mode === 'csv-upload' && (
          <div className="mb-4">
            <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-6 text-center">
              <input type="file" accept=".csv" onChange={handleFileUpload} className="hidden" id="csv-upload" />
              <label htmlFor="csv-upload" className="cursor-pointer">
                <p className="text-gray-500 dark:text-gray-400 mb-2">Click to upload a CSV file</p>
                <p className="text-xs text-gray-400 dark:text-gray-500">Supports quoted fields, commas in values, and most CSV exports from Excel or Google Sheets.</p>
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">After uploading, you'll map each column to the correct field before importing.</p>
              </label>
            </div>
          </div>
        )}
        {mode === 'manual' && (
          <>
            <div className="space-y-3 mb-4">
              {rows.map((row, i) => (
                <ImportEmployeeCard key={i} row={row} index={i} onUpdate={updateRow} onRemove={removeRow} canRemove={rows.length > 1} />
              ))}
            </div>
            <button onClick={addRow} className="text-sm text-brand-600 hover:text-brand-800 font-medium mb-4">+ Add Another Employee</button>
            {error && <p className="text-red-500 text-sm mb-3">{error}</p>}
            <div className="flex justify-end space-x-3 pt-2 border-t">
              <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600">Cancel</button>
              <button onClick={handleSubmit} disabled={submitting} className="px-4 py-2 text-sm text-white bg-brand-600 rounded-md hover:bg-brand-700 disabled:opacity-50">
                {submitting ? 'Importing...' : `Import ${rows.filter(r => r.first_name && r.last_name).length} Employee(s)`}
              </button>
            </div>
          </>
        )}
        {mode === 'csv-upload' && (
          <div className="flex justify-end space-x-3 pt-2 border-t">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600">Cancel</button>
          </div>
        )}
      </div>
    </div>
  );
}

function formatPayDay(company) {
  if (!company.pay_day_type) return '-';
  if (company.pay_day_type === 'specific_date' && company.pay_day_specific_date) {
    const d = company.pay_day_specific_date;
    const suffix = d === 1 || d === 21 ? 'st' : d === 2 || d === 22 ? 'nd' : d === 3 || d === 23 ? 'rd' : 'th';
    return `${d}${suffix}`;
  }
  return PAY_DAY_LABELS[company.pay_day_type] || company.pay_day_type;
}


// ─────────────────────────────────────────────────────────────────────────
// Clients list (Phase 5 redesign) — designed to scan at 300+ clients.
// All modals above (InviteClient/InviteUser/PayDay/ImportEmployees) are reused.
// ─────────────────────────────────────────────────────────────────────────

const FREQ_SHORT = { monthly: 'M', weekly: 'W', fortnightly: 'F', '4_weekly': '4W' };

function starsKey(userId) { return `sa_starred_clients_${userId || 'anon'}`; }
function loadStars(userId) {
  try { return new Set(JSON.parse(localStorage.getItem(starsKey(userId)) || '[]')); }
  catch { return new Set(); }
}
function saveStars(userId, set) {
  try { localStorage.setItem(starsKey(userId), JSON.stringify([...set])); } catch { /* ignore */ }
}

function clientContactName(c) {
  const n = c.contact_first_name ? `${c.contact_first_name} ${c.contact_last_name || ''}`.trim() : c.contact_name;
  return n || '—';
}
function companyLetter(c) {
  const ch = (c.company_name || '').trim().charAt(0).toUpperCase();
  return /[A-Z]/.test(ch) ? ch : '#';
}
function isDraftCompany(c) { return !c.any_user_accepted; }

// Client *setup* status — reflects where a company is in onboarding, NOT this
// month's payroll submission (that's the Dashboard's job). A company is "active"
// once any user has accepted their invite; otherwise it's a draft in one of a
// few sub-states.
function setupStatusOf(c) {
  if (c.any_user_accepted) return 'active';
  if (parseInt(c.user_count) === 0) return 'no_users';
  return c.has_pending_invite ? 'invite_pending' : 'draft';
}

const CLIENT_STATUS = {
  active:         { label: 'Active',         cls: 'bg-emerald-100 text-emerald-800 ring-1 ring-emerald-200' },
  invite_pending: { label: 'Invite pending', cls: 'bg-amber-100 text-amber-800 ring-1 ring-amber-200' },
  no_users:       { label: 'No users',       cls: 'bg-rose-100 text-rose-800 ring-1 ring-rose-200' },
  draft:          { label: 'Draft',          cls: 'bg-gray-100 text-gray-700 ring-1 ring-gray-200' },
};

function ClientStatusPill({ status }) {
  const s = CLIENT_STATUS[status] || CLIENT_STATUS.draft;
  return (
    <span className={`inline-flex items-center rounded font-semibold tracking-wide px-1.5 py-0.5 text-[13px] ${s.cls}`}>
      {s.label}
    </span>
  );
}

const FEATURE_DEFS = [
  { key: 'send_payroll_reminder', label: 'Auto reminders on', cls: 'bg-brand-100 text-brand-700' },
  { key: 'commission_enabled', label: 'Commission tracking', cls: 'bg-emerald-100 text-emerald-700' },
  { key: 'hourly_tracking_enabled', label: 'Hourly tracking', cls: 'bg-sky-100 text-sky-700' },
];

function FeatureGlyph({ k }) {
  if (k === 'commission_enabled') {
    return <span className="text-[12px] font-bold leading-none">£</span>;
  }
  if (k === 'hourly_tracking_enabled') {
    return (
      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="9" /><path d="M12 8v4l2.5 1.5" />
      </svg>
    );
  }
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" /><path d="M13.7 21a2 2 0 0 1-3.4 0" />
    </svg>
  );
}

function FeatureIcons({ company }) {
  const on = FEATURE_DEFS.filter(f => company[f.key]);
  if (on.length === 0) return <span className="text-[12px] text-gray-400 italic">— setup pending</span>;
  return (
    <div className="flex items-center gap-1">
      {on.map(f => (
        <span key={f.key} title={f.label} className={`grid place-items-center w-5 h-5 rounded ${f.cls}`}>
          <FeatureGlyph k={f.key} />
        </span>
      ))}
    </div>
  );
}

function Toggle({ on, onClick }) {
  return (
    <button onClick={onClick} className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors ${on ? 'bg-emerald-500' : 'bg-gray-300'}`}>
      <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${on ? 'translate-x-5' : 'translate-x-1'}`} />
    </button>
  );
}

// Registered-users management — lifted unchanged from the previous expandable row.
function ClientUsersPanel({ company, onReloadCompanies, onShowToast, onEditAccess }) {
  const [users, setUsers] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [localConfirm, setLocalConfirm] = useState(null);
  const [editingUser, setEditingUser] = useState(null);

  useEffect(() => {
    let alive = true;
    api.get(`/admin/companies/${company.id}/users`)
      .then(res => { if (alive) setUsers(res.data); })
      .catch(err => console.error('Failed to load users:', err))
      .finally(() => { if (alive) setLoaded(true); });
    return () => { alive = false; };
  }, [company.id]);

  const displayName = (u) => u.first_name ? `${u.first_name} ${u.last_name || ''}`.trim() : u.name;
  const isPrimaryContact = (u) => u.email === company.email;

  const handleResendInvite = async (userId) => {
    try { await api.post(`/admin/resend-invite/${userId}`); onShowToast({ message: 'Invite resent successfully', type: 'success' }); }
    catch (err) { onShowToast({ message: err.response?.data?.error || 'Failed to resend invite', type: 'error' }); }
  };

  const handleDeleteUser = (userId, userName, isPrimary) => {
    const isSharedUser = !isPrimary;
    setLocalConfirm({
      title: isSharedUser ? 'Revoke Access' : 'Remove User',
      message: isSharedUser
        ? `Remove ${userName}'s access to ${company.company_name}? They will still have access to their other clients.`
        : `Are you sure you want to remove ${userName}? This cannot be undone.`,
      confirmText: isSharedUser ? 'Revoke Access' : 'Remove',
      confirmStyle: 'danger',
      onConfirm: async () => {
        setLocalConfirm(null);
        try { await api.delete(`/admin/users/${userId}?company_id=${company.id}`); setUsers(users.filter(u => u.id !== userId)); }
        catch (err) { onShowToast({ message: err.response?.data?.error || 'Failed to remove user', type: 'error' }); }
      },
    });
  };

  const handleReset2FA = (userId, userName) => {
    setLocalConfirm({
      title: 'Reset Two-Factor Authentication',
      message: `Reset 2FA for ${userName}? Their current authenticator setup will be removed so they can set it up again (or sign in without it). Use this if they're locked out of their authenticator app.`,
      confirmText: 'Reset 2FA', confirmStyle: 'danger',
      onConfirm: async () => {
        setLocalConfirm(null);
        try {
          await api.post(`/admin/users/${userId}/reset-2fa`);
          setUsers(users.map(u => u.id === userId ? { ...u, totp_enabled: false } : u));
          onShowToast({ message: `2FA reset for ${userName}`, type: 'success' });
        } catch (err) { onShowToast({ message: err.response?.data?.error || 'Failed to reset 2FA', type: 'error' }); }
      },
    });
  };

  const handleSetPrimaryContact = (userId, userName) => {
    setLocalConfirm({
      title: 'Set Primary Contact',
      message: `Make ${userName} the primary contact for ${company.company_name}? This will update the company's contact details and email.`,
      confirmText: 'Confirm', confirmStyle: 'primary',
      onConfirm: async () => {
        setLocalConfirm(null);
        try {
          await api.patch(`/admin/companies/${company.id}/primary-contact`, { user_id: userId });
          onShowToast({ message: `${userName} is now the primary contact`, type: 'success' });
          onReloadCompanies();
        } catch (err) { onShowToast({ message: err.response?.data?.error || 'Failed to update primary contact', type: 'error' }); }
      },
    });
  };

  const handleSaveUserDetails = async (userId) => {
    if (!editingUser) return;
    try {
      const res = await api.patch(`/admin/users/${userId}/details`, {
        first_name: editingUser.first_name, last_name: editingUser.last_name, email: editingUser.email,
      });
      setUsers(users.map(u => u.id === userId ? { ...u, ...res.data } : u));
      setEditingUser(null);
      onShowToast({ message: 'User details updated', type: 'success' });
      onReloadCompanies();
    } catch (err) { onShowToast({ message: err.response?.data?.error || 'Failed to update user', type: 'error' }); }
  };

  return (
    <div>
      <div className="text-xs font-medium text-gray-500 uppercase mb-2">Registered users (reminders sent to all)</div>
      {!loaded ? (
        <p className="text-sm text-gray-400">Loading users…</p>
      ) : users.length === 0 ? (
        <p className="text-sm text-gray-400">No users registered yet.</p>
      ) : (
        <div className="space-y-1">
          {users.map(u => (
            <div key={u.id} className="flex items-center justify-between p-2 bg-white rounded border text-sm">
              <div className="flex items-center space-x-3">
                {editingUser?.id === u.id ? (
                  <>
                    <input value={editingUser.first_name} onChange={(e) => setEditingUser(prev => ({ ...prev, first_name: e.target.value }))} placeholder="First name" className="w-24 text-sm border border-gray-300 rounded px-1.5 py-0.5" />
                    <input value={editingUser.last_name} onChange={(e) => setEditingUser(prev => ({ ...prev, last_name: e.target.value }))} placeholder="Last name" className="w-24 text-sm border border-gray-300 rounded px-1.5 py-0.5" />
                    <input value={editingUser.email} onChange={(e) => setEditingUser(prev => ({ ...prev, email: e.target.value }))} placeholder="Email" className="w-48 text-sm border border-gray-300 rounded px-1.5 py-0.5" />
                    <button onClick={() => handleSaveUserDetails(u.id)} className="text-xs text-green-600 hover:text-green-800 font-medium">Save</button>
                    <button onClick={() => setEditingUser(null)} className="text-xs text-gray-400 hover:text-gray-600">Cancel</button>
                  </>
                ) : (
                  <>
                    <span className="font-medium">{displayName(u)}</span>
                    <span className="text-gray-500">{u.email}</span>
                    <button onClick={() => setEditingUser({ id: u.id, first_name: u.first_name || '', last_name: u.last_name || '', email: u.email })} className="text-xs text-gray-400 hover:text-brand-600">Edit</button>
                  </>
                )}
                <select value={u.role} onChange={async (e) => {
                  try { await api.patch(`/admin/users/${u.id}/client-role`, { role: e.target.value }); setUsers(users.map(usr => usr.id === u.id ? { ...usr, role: e.target.value } : usr)); }
                  catch (err) { onShowToast({ message: err.response?.data?.error || 'Failed to update role', type: 'error' }); }
                }} className="text-xs border border-gray-200 rounded px-1.5 py-0.5 text-gray-600 bg-white cursor-pointer hover:border-brand-400">
                  <option value="company_admin">Admin</option>
                  <option value="user">User</option>
                </select>
              </div>
              <div className="flex items-center space-x-3">
                {u.invite_accepted ? (
                  <span className="text-xs text-green-600 font-medium">Active</span>
                ) : (
                  <div className="flex items-center space-x-2">
                    <span className="text-xs text-amber-600 font-medium">Invite Pending</span>
                    <button onClick={() => handleResendInvite(u.id)} className="text-xs text-brand-600 hover:text-brand-800 underline">Resend</button>
                  </div>
                )}
                {isPrimaryContact(u) ? (
                  <span className="text-xs text-brand-600 font-medium bg-brand-50 px-1.5 py-0.5 rounded">Primary Contact</span>
                ) : (
                  <button onClick={() => handleSetPrimaryContact(u.id, displayName(u))} className="text-xs text-gray-400 hover:text-brand-600 font-medium">Set as Primary</button>
                )}
                {!u.is_primary && <span className="text-xs text-purple-500 font-medium">Shared</span>}
                {u.totp_enabled && (
                  <button onClick={() => handleReset2FA(u.id, displayName(u))} className="text-xs text-amber-600 hover:text-amber-800 font-medium">Reset 2FA</button>
                )}
                <button onClick={() => onEditAccess(u)} className="text-xs text-brand-600 hover:text-brand-800 font-medium">Edit Access</button>
                <button onClick={() => handleDeleteUser(u.id, displayName(u), u.is_primary)} className="text-xs text-red-500 hover:text-red-700">Remove</button>
              </div>
            </div>
          ))}
        </div>
      )}
      {localConfirm && (
        <ConfirmModal title={localConfirm.title} message={localConfirm.message} confirmText={localConfirm.confirmText}
          confirmStyle={localConfirm.confirmStyle} onConfirm={localConfirm.onConfirm} onCancel={() => setLocalConfirm(null)} />
      )}
    </div>
  );
}

function ActionIconButton({ title, onClick, children }) {
  return (
    <button title={title} onClick={onClick}
      className="grid place-items-center w-7 h-7 rounded-md text-gray-400 hover:text-brand-700 hover:bg-brand-50">
      {children}
    </button>
  );
}

function ClientRow({ company, status, starred, onToggleStar, onViewAs, onSendReminder, onEditPayDay,
                     onEditPensionPolicy, onToggleReminder, onToggleCommission, onToggleHourly,
                     onImportEmployees, onDelete,
                     onReloadCompanies, onShowToast, onEditAccess }) {
  const [expanded, setExpanded] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const h = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [menuOpen]);

  const headcount = parseInt(company.employee_count) || 0;
  const freq = company.pay_frequency || 'monthly';

  const menuItem = 'w-full flex items-center justify-between gap-3 px-3 py-1.5 text-[13px] text-gray-700 hover:bg-gray-50 text-left';

  return (
    <>
      <tr className="border-t border-gray-100 hover:bg-brand-50/40 align-middle" style={{ height: 52 }}>
        <td className="pl-4 pr-1">
          <button title={starred ? 'Unstar' : 'Star'} onClick={() => onToggleStar(company.id)} className="grid place-items-center w-6 h-6">
            <svg width="16" height="16" viewBox="0 0 24 24" fill={starred ? '#f59e0b' : 'none'} stroke={starred ? '#f59e0b' : '#cbd5e1'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
            </svg>
          </button>
        </td>
        <td className="px-3 py-2">
          <div className="flex items-center gap-2">
            <button onClick={() => setExpanded(v => !v)} title="Manage users"
              className="text-gray-300 hover:text-gray-500 text-[10px] w-3"
              style={{ transform: expanded ? 'rotate(90deg)' : 'none', transition: 'transform .15s' }}>&#9654;</button>
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <button onClick={onViewAs} className="text-[13.5px] font-semibold text-gray-900 hover:text-brand-700 truncate">{company.company_name}</button>
              </div>
              <div className="text-[12px] text-gray-400">{headcount} empl.</div>
            </div>
          </div>
        </td>
        <td className="px-3 py-2">
          <div className="text-[13px] text-gray-700">{clientContactName(company)}</div>
          {company.email && <a href={`mailto:${company.email}`} className="text-[12.5px] text-brand-700 hover:underline">{company.email}</a>}
        </td>
        <td className="px-3 py-2 text-right tabular font-semibold text-[13.5px] text-gray-800 w-16">{headcount}</td>
        <td className="px-3 py-2">
          <div className="flex items-center gap-2">
            <MiniPill tone={freq === 'weekly' ? 'sky' : 'brand'}>{FREQ_SHORT[freq] || 'M'}</MiniPill>
            <span className="text-[13px] text-gray-600">{formatPayDay(company)}</span>
          </div>
        </td>
        <td className="px-3 py-2 w-28"><FeatureIcons company={company} /></td>
        <td className="px-3 py-2"><ClientStatusPill status={status} /></td>
        <td className="px-3 py-2">
          <div className="flex items-center justify-end gap-1">
            <ActionIconButton title="View as this client" onClick={onViewAs}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></svg>
            </ActionIconButton>
            <ActionIconButton title="Send reminder" onClick={() => onSendReminder(company.id, company.company_name)}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" /><path d="M13.7 21a2 2 0 0 1-3.4 0" /></svg>
            </ActionIconButton>
            <div className="relative" ref={menuRef}>
              <ActionIconButton title="More" onClick={() => setMenuOpen(v => !v)}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><circle cx="5" cy="12" r="1.6" /><circle cx="12" cy="12" r="1.6" /><circle cx="19" cy="12" r="1.6" /></svg>
              </ActionIconButton>
              {menuOpen && (
                <div className="absolute right-0 mt-1 w-56 bg-white rounded-lg shadow-2xl ring-1 ring-gray-200 z-20 py-1">
                  <button className={menuItem} onClick={() => { setMenuOpen(false); onEditPayDay(company); }}><span>Pay schedule</span></button>
                  <button className={menuItem} onClick={() => { setMenuOpen(false); onEditPensionPolicy(company); }}><span>Pension policy</span></button>
                  <button className={menuItem} onClick={() => { setMenuOpen(false); onImportEmployees(company.id, company.company_name); }}><span>Import staff (CSV)</span></button>
                  <button className={menuItem} onClick={() => { setMenuOpen(false); setExpanded(true); }}><span>Manage users</span></button>
                  <div className="my-1 border-t border-gray-100" />
                  <div className={menuItem}><span>Auto reminders</span><Toggle on={company.send_payroll_reminder} onClick={() => onToggleReminder(company.id, company.send_payroll_reminder)} /></div>
                  <div className={menuItem}><span>Commission tracking</span><Toggle on={company.commission_enabled} onClick={() => onToggleCommission(company.id, company.commission_enabled)} /></div>
                  <div className={menuItem}><span>Hourly tracking</span><Toggle on={company.hourly_tracking_enabled} onClick={() => onToggleHourly(company.id, company.hourly_tracking_enabled)} /></div>
                  <div className="my-1 border-t border-gray-100" />
                  <button className="w-full flex items-center px-3 py-1.5 text-[13px] text-rose-600 hover:bg-rose-50 text-left" onClick={() => { setMenuOpen(false); onDelete(company.id, company.company_name); }}>Remove client</button>
                </div>
              )}
            </div>
          </div>
        </td>
      </tr>
      {expanded && (
        <tr className="bg-gray-50">
          <td colSpan={8} className="px-8 py-3">
            <ClientUsersPanel company={company} onReloadCompanies={onReloadCompanies} onShowToast={onShowToast} onEditAccess={onEditAccess} />
          </td>
        </tr>
      )}
    </>
  );
}

function LetterDivider({ letter }) {
  return (
    <tr id={`letter-${letter}`}>
      <td colSpan={8} className="bg-gradient-to-r from-brand-50 to-white px-4 py-1">
        <span className="text-[11px] font-bold tracking-wider text-brand-700 uppercase">{letter}</span>
      </td>
    </tr>
  );
}

const VIEW_TABS = [
  { key: 'all', label: 'All clients' },
  { key: 'active', label: 'Active' },
  { key: 'drafts', label: 'Drafts', tone: 'rose' },
  { key: 'weekly', label: 'Weekly payers' },
  { key: 'starred', label: 'Starred', star: true },
];

export default function ClientsPage() {
  const { user, viewAsClient } = useAuth();
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState({ message: '', type: 'error' });
  const [confirmAction, setConfirmAction] = useState(null);

  const [showInviteClient, setShowInviteClient] = useState(false);
  const [showInviteUser, setShowInviteUser] = useState(false);
  const [importTarget, setImportTarget] = useState(null);
  const [payDayTarget, setPayDayTarget] = useState(null);
  const [pensionPolicyTarget, setPensionPolicyTarget] = useState(null);
  const [editAccessUser, setEditAccessUser] = useState(null);

  const [search, setSearch] = useState('');
  const [tab, setTab] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [freqFilter, setFreqFilter] = useState('all');
  const [sort, setSort] = useState('name_asc');
  const [stars, setStars] = useState(() => loadStars(user?.id));
  // Re-load starred clients once auth resolves — the initial useState runs with
  // user undefined, so without this the page reads the `…_anon` key forever.
  useEffect(() => { setStars(loadStars(user?.id)); }, [user?.id]);
  const searchRef = useRef(null);

  const reloadCompanies = useCallback(async () => {
    try { const res = await api.get('/admin/companies'); setCompanies(res.data); }
    catch (err) { console.error('Failed to load companies:', err); }
  }, []);

  // The Clients page is about client setup/lifecycle, not this month's payroll
  // workflow — so it only needs the company roster. Current-month submission
  // status lives on the Admin Dashboard.
  const loadAll = useCallback(async () => {
    try {
      const res = await api.get('/admin/companies');
      setCompanies(res.data);
    } catch (err) { console.error('Failed to load clients:', err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  // ⌘K / Ctrl-K focuses search
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') { e.preventDefault(); searchRef.current?.focus(); }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  const toggleStar = (id) => {
    setStars(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      saveStars(user?.id, next);
      return next;
    });
  };

  const handleToggleReminder = async (companyId, currentValue) => {
    try { await api.patch(`/admin/companies/${companyId}/notifications`, { send_payroll_reminder: !currentValue }); reloadCompanies(); }
    catch (err) { setToast({ message: 'Failed to update: ' + (err.response?.data?.error || err.message), type: 'error' }); }
  };
  const handleToggleCommission = async (companyId, currentValue) => {
    try { await api.patch(`/admin/companies/${companyId}/commission`, { commission_enabled: !currentValue }); reloadCompanies(); }
    catch (err) { setToast({ message: 'Failed to update: ' + (err.response?.data?.error || err.message), type: 'error' }); }
  };
  const handleToggleHourly = async (companyId, currentValue) => {
    try { await api.patch(`/admin/companies/${companyId}/hourly-tracking`, { hourly_tracking_enabled: !currentValue }); reloadCompanies(); }
    catch (err) { setToast({ message: 'Failed to update: ' + (err.response?.data?.error || err.message), type: 'error' }); }
  };
  const handleSendReminder = async (companyId, name) => {
    const now = new Date();
    try {
      const res = await api.post(`/admin/send-reminder/${companyId}`, { month: now.getMonth() + 1, year: now.getFullYear() });
      setToast({ message: res.data?.message || `Reminder sent to ${name}`, type: 'success' });
    } catch (err) { setToast({ message: err.response?.data?.error || 'Failed to send reminder', type: 'error' }); }
  };
  const handleDeleteCompany = (companyId, companyName) => {
    setConfirmAction({
      title: 'Remove Client',
      message: `This will permanently remove "${companyName}" and all associated employees, submissions, and documents.\n\nThis cannot be undone.`,
      confirmText: 'Remove Client', confirmStyle: 'danger', requireType: 'DELETE',
      onConfirm: async () => {
        setConfirmAction(null);
        try { await api.delete(`/admin/companies/${companyId}`); loadAll(); }
        catch (err) { setToast({ message: err.response?.data?.error || 'Failed to remove company', type: 'error' }); }
      },
    });
  };

  const tabCounts = useMemo(() => {
    const c = { all: companies.length, active: 0, drafts: 0, weekly: 0, starred: 0 };
    companies.forEach(co => {
      if (isDraftCompany(co)) c.drafts += 1; else c.active += 1;
      if ((co.pay_frequency || 'monthly') === 'weekly') c.weekly += 1;
      if (stars.has(co.id)) c.starred += 1;
    });
    return c;
  }, [companies, stars]);

  const visible = useMemo(() => {
    let list = companies.filter(co => {
      switch (tab) {
        case 'active': return !isDraftCompany(co);
        case 'drafts': return isDraftCompany(co);
        case 'weekly': return (co.pay_frequency || 'monthly') === 'weekly';
        case 'starred': return stars.has(co.id);
        default: return true;
      }
    });
    if (statusFilter === 'active') list = list.filter(c => !isDraftCompany(c));
    else if (statusFilter === 'draft') list = list.filter(isDraftCompany);
    if (freqFilter !== 'all') list = list.filter(c => (c.pay_frequency || 'monthly') === freqFilter);
    const q = search.trim().toLowerCase();
    if (q) list = list.filter(c =>
      (c.company_name || '').toLowerCase().includes(q) ||
      (c.email || '').toLowerCase().includes(q) ||
      clientContactName(c).toLowerCase().includes(q) ||
      (c.id || '').toLowerCase().includes(q));
    const sorted = [...list];
    sorted.sort((a, b) => {
      if (sort === 'name_desc') return (b.company_name || '').localeCompare(a.company_name || '');
      if (sort === 'empl_desc') return (parseInt(b.employee_count) || 0) - (parseInt(a.employee_count) || 0);
      return (a.company_name || '').localeCompare(b.company_name || '');
    });
    return sorted;
  }, [companies, tab, statusFilter, freqFilter, search, sort, stars]);

  const showLetters = sort === 'name_asc' || sort === 'name_desc';
  const presentLetters = useMemo(() => {
    const s = new Set();
    if (showLetters) visible.forEach(c => s.add(companyLetter(c)));
    return s;
  }, [visible, showLetters]);

  const total = companies.length;
  const draftCount = companies.filter(isDraftCompany).length;
  const activeCount = total - draftCount;
  const weeklyCount = companies.filter(c => (c.pay_frequency || 'monthly') === 'weekly').length;

  const jumpToLetter = (L) => {
    const el = document.getElementById(`letter-${L}`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  const rows = [];
  let lastLetter = null;
  visible.forEach(c => {
    const L = companyLetter(c);
    if (showLetters && L !== lastLetter) { rows.push(<LetterDivider key={`d-${L}`} letter={L} />); lastLetter = L; }
    rows.push(
      <ClientRow key={c.id} company={c} status={setupStatusOf(c)} starred={stars.has(c.id)}
        onToggleStar={toggleStar} onViewAs={() => viewAsClient(c.id)} onSendReminder={handleSendReminder}
        onEditPayDay={(co) => setPayDayTarget(co)} onEditPensionPolicy={(co) => setPensionPolicyTarget(co)}
        onToggleReminder={handleToggleReminder}
        onToggleCommission={handleToggleCommission} onToggleHourly={handleToggleHourly}
        onImportEmployees={(id, name) => setImportTarget({ id, name })} onDelete={handleDeleteCompany}
        onReloadCompanies={reloadCompanies} onShowToast={setToast} onEditAccess={(u) => setEditAccessUser(u)} />
    );
  });

  if (loading) return <div className="text-center py-12 text-gray-500">Loading clients...</div>;

  const selectCls = 'text-[12.5px] rounded-md ring-1 ring-gray-200 bg-white text-gray-700 px-2.5 py-1.5 focus:ring-brand-400';

  return (
    <div className="max-w-[1500px] mx-auto px-8 py-7">
      <Toast message={toast.message} type={toast.type} onClose={() => setToast({ message: '', type: 'error' })} />

      <header className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <h1 className="font-heading text-[28px] leading-none text-ink">Clients</h1>
          <p className="text-gray-500 text-[14px] mt-2">
            Every company you manage payroll for. Showing <strong className="text-gray-700">{total - draftCount} active</strong> · <strong className="text-gray-700">{draftCount} draft{draftCount === 1 ? '' : 's'}</strong>.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setShowInviteUser(true)} className="px-4 py-2 text-[13.5px] font-medium text-brand-700 bg-brand-50 rounded-md hover:bg-brand-100">Invite user</button>
          <button onClick={() => setShowInviteClient(true)} className="px-4 py-2 text-[13.5px] font-medium text-white bg-brand-600 rounded-md hover:bg-brand-700">+ New client</button>
        </div>
      </header>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <KpiTile label="Total clients" value={total} sub="all companies" tone="brand"
          onClick={() => setTab('all')} active={tab === 'all'} />
        <KpiTile label="Active" value={activeCount} sub="onboarded" tone="emerald"
          onClick={() => setTab('active')} active={tab === 'active'} />
        <KpiTile label="Weekly payers" value={weeklyCount} sub="weekly schedule" tone="sky"
          onClick={() => setTab('weekly')} active={tab === 'weekly'} />
        <KpiTile label="Drafts" value={draftCount} sub="need setup" tone="rose" alert={draftCount > 0}
          onClick={() => setTab('drafts')} active={tab === 'drafts'} />
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-4">
        {VIEW_TABS.map(t => {
          const active = tab === t.key;
          const count = tabCounts[t.key];
          return (
            <button key={t.key} onClick={() => setTab(t.key)}
              className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-md text-[13px] font-medium transition-colors ${active ? 'bg-ink text-white' : 'text-gray-600 hover:bg-gray-50'}`}>
              {t.star && <span className={active ? 'text-amber-300' : 'text-amber-400'}>★</span>}
              {t.label}
              <span className={`text-[11.5px] px-1.5 rounded-full ${active ? 'bg-white/20' : t.tone === 'rose' ? 'bg-rose-100 text-rose-700' : 'bg-gray-100 text-gray-600'}`}>{count}</span>
            </button>
          );
        })}
      </div>

      <div className="sticky top-0 z-10 bg-white ring-1 ring-gray-200 rounded-xl px-4 py-2.5 mb-4 flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[220px]">
          <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="7" /><path d="m21 21-4.3-4.3" /></svg>
          </span>
          <input ref={searchRef} value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, contact email, or ID…"
            className="w-full pl-8 pr-12 py-1.5 text-[13.5px] rounded-md ring-1 ring-gray-200 focus:ring-brand-400 focus:outline-none" />
          <kbd className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[11px] text-gray-400 ring-1 ring-gray-200 rounded px-1">⌘K</kbd>
        </div>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className={selectCls}>
          <option value="all">Status: All</option>
          <option value="active">Active</option>
          <option value="draft">Draft</option>
        </select>
        <select value={freqFilter} onChange={(e) => setFreqFilter(e.target.value)} className={selectCls}>
          <option value="all">Frequency: All</option>
          <option value="monthly">Monthly</option>
          <option value="weekly">Weekly</option>
          <option value="fortnightly">Fortnightly</option>
          <option value="4_weekly">4-Weekly</option>
        </select>
        <select value={sort} onChange={(e) => setSort(e.target.value)} className={selectCls}>
          <option value="name_asc">Sort: Name A→Z</option>
          <option value="name_desc">Name Z→A</option>
          <option value="empl_desc">Headcount</option>
        </select>
      </div>

      <div className="flex gap-2 items-start">
        <div className="flex-1 min-w-0 bg-white rounded-xl ring-1 ring-gray-200 overflow-hidden">
          <table className="min-w-full">
            <thead className="bg-gray-50 text-gray-500">
              <tr className="text-left text-[11.5px] uppercase tracking-wider">
                <th className="pl-4 pr-1 py-2 w-9"></th>
                <th className="px-3 py-2 font-bold">Client</th>
                <th className="px-3 py-2 font-bold">Primary contact</th>
                <th className="px-3 py-2 font-bold text-right w-16">Empl.</th>
                <th className="px-3 py-2 font-bold">Pay schedule</th>
                <th className="px-3 py-2 font-bold w-28">Features</th>
                <th className="px-3 py-2 font-bold">Status</th>
                <th className="px-3 py-2 font-bold text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows}
              {visible.length === 0 && (
                <tr><td colSpan={8} className="px-4 py-10 text-center text-gray-500">No clients match this view.</td></tr>
              )}
            </tbody>
          </table>
        </div>
        {showLetters && (
          <div className="w-7 sticky top-16 self-start hidden md:flex flex-col items-center text-[10px] font-semibold text-gray-300 py-1">
            {'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('').map(L => (
              <button key={L} disabled={!presentLetters.has(L)} onClick={() => jumpToLetter(L)}
                className={presentLetters.has(L) ? 'text-brand-600 hover:text-brand-800 leading-tight' : 'text-gray-200 cursor-default leading-tight'}>{L}</button>
            ))}
          </div>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-4 mt-3 text-[12px] text-gray-500">
        <span className="font-semibold text-gray-400 uppercase tracking-wider text-[11px]">Features</span>
        {FEATURE_DEFS.map(f => (
          <span key={f.key} className="inline-flex items-center gap-1.5">
            <span className={`grid place-items-center w-4 h-4 rounded ${f.cls}`}><FeatureGlyph k={f.key} /></span>
            {f.label}
          </span>
        ))}
        <span className="ml-auto text-gray-400">Showing {visible.length} of {total}</span>
      </div>

      {showInviteClient && <InviteClientModal onClose={() => setShowInviteClient(false)} onSuccess={(msg) => { setShowInviteClient(false); loadAll(); setToast({ message: msg || 'Client created successfully!', type: 'success' }); }} />}
      {showInviteUser && <InviteUserModal companies={companies} onClose={() => setShowInviteUser(false)} onSuccess={() => { setShowInviteUser(false); loadAll(); setToast({ message: 'Invitation sent successfully!', type: 'success' }); }} />}
      {importTarget && <ImportEmployeesModal companyId={importTarget.id} companyName={importTarget.name} onClose={() => setImportTarget(null)} onSuccess={() => setImportTarget(null)} />}
      {payDayTarget && <PayDayModal company={payDayTarget} onClose={() => setPayDayTarget(null)} onSuccess={() => { setPayDayTarget(null); reloadCompanies(); }} />}
      {pensionPolicyTarget && <PensionPolicyModal company={pensionPolicyTarget} onClose={() => setPensionPolicyTarget(null)} onSuccess={() => { setPensionPolicyTarget(null); reloadCompanies(); setToast({ message: 'Pension policy updated', type: 'success' }); }} />}
      {editAccessUser && <ManageUserAccessModal user={editAccessUser} companies={companies} onClose={() => setEditAccessUser(null)} onSuccess={() => { setEditAccessUser(null); reloadCompanies(); setToast({ message: 'Client access updated successfully!', type: 'success' }); }} />}
      {confirmAction && (
        <ConfirmModal title={confirmAction.title} message={confirmAction.message} confirmText={confirmAction.confirmText}
          confirmStyle={confirmAction.confirmStyle} requireType={confirmAction.requireType} onConfirm={confirmAction.onConfirm} onCancel={() => setConfirmAction(null)} />
      )}
    </div>
  );
}
